import React, { useMemo } from 'react';
import classNames from 'classnames';

import { Leg, Marker, RouteData } from './common';

interface ComponentProps {
  routeData: RouteData;
  markers: Marker[];
}

const RouteInfo: React.FC<ComponentProps> = ({ routeData, markers }) => {
  const { leg_overviews } = routeData;

  const totals = useMemo(() => {
    if (!leg_overviews?.length) return { distance: 0, duration: 0, count: 0 };
    return leg_overviews.reduce(
      (acc, leg: Leg) => ({
        distance: acc.distance + leg.distance,
        duration: acc.duration + leg.duration,
        count: acc.count + 1,
      }),
      { distance: 0, duration: 0, count: 0 },
    );
  }, [leg_overviews]);

  const stops = useMemo(() => {
    if (!leg_overviews?.length) return [];
    const result: { name: string }[] = [];

    const originName =
      markers.find((m) => m.type === 'origin')?.data.name || leg_overviews[0]?.waypoint_from.name || 'Stop 0';
    result.push({ name: originName });

    leg_overviews.forEach((leg: Leg, i: number) => {
      const isLast = i === leg_overviews.length - 1;
      const stopName =
        markers.find((m) => m.orderId === i + 1)?.data.name ||
        (isLast ? markers.find((m) => m.type === 'origin')?.data.name : undefined) ||
        leg.waypoint_to.name ||
        `Stop ${i + 1}`;
      result.push({ name: stopName });
    });

    return result;
  }, [leg_overviews, markers]);

  return (
    <div>
      <h6 className="fw-bold mb-2">Route Information</h6>
      {leg_overviews && leg_overviews.length > 0 ? (
        <>
          <div className="text-muted small mb-3">
            Total: <strong>{totals.distance.toFixed(1)} km</strong>
            {' · '}
            <strong>{totals.duration} min</strong>
            {' · '}
            {totals.count} {totals.count === 1 ? 'leg' : 'legs'}
          </div>
          <ol className="route-timeline" aria-label="Route stops">
            {stops.map((stop, idx) => {
              const isFinal = idx === stops.length - 1;
              const leg = !isFinal ? leg_overviews[idx] : null;
              return (
                <li
                  key={`stop-${idx}`}
                  className={classNames('route-stop-wrapper', isFinal && 'route-stop-final')}
                >
                  <div className="route-stop">
                    <span className="route-num" aria-hidden="true">
                      {idx + 1}
                    </span>
                    <span className="route-text">{stop.name}</span>
                  </div>
                  {leg && (
                    <div
                      className="route-leg"
                      aria-label={`${leg.distance.toFixed(1)} kilometres, ${leg.duration} minutes to next stop`}
                    >
                      <span className="route-arrow" aria-hidden="true">
                        ↓
                      </span>
                      <span className="route-detail">
                        {leg.distance.toFixed(1)} km · {leg.duration} min
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      ) : (
        <p className="text-muted mb-0">No route legs available.</p>
      )}
    </div>
  );
};

export default RouteInfo;
